
import { Card, CardContent, CardFooter, CardHeader } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { AlertCircle, Calendar, Clock, Edit, MessageSquare } from "lucide-react";
import { Task } from "../../types/tasks";

interface TaskCardProps {
  task: Task;
  onStatusChange: (id: string, completed: boolean) => void;
  onView: (id: string) => void;
  isAdminView?: boolean;
  currentUserId?: string;
}

const TaskCard = ({ task, onStatusChange, onView, isAdminView = false, currentUserId }: TaskCardProps) => {
  const getStatusColor = (status: string) => {
    switch (status) {
      case "completed":
        return "bg-green-100 text-green-800 border-green-200";
      case "in_progress":
        return "bg-blue-100 text-blue-800 border-blue-200";
      case "pending":
        return "bg-amber-100 text-amber-800 border-amber-200";
      case "overdue":
        return "bg-red-100 text-red-800 border-red-200";
      default:
        return "bg-gray-100 text-gray-800 border-gray-200";
    }
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case "high":
        return "bg-red-100 text-red-800 border-red-200";
      case "medium":
        return "bg-amber-100 text-amber-800 border-amber-200";
      case "low":
        return "bg-green-100 text-green-800 border-green-200";
      default:
        return "bg-gray-100 text-gray-800 border-gray-200";
    }
  };

  // Determine if current user should be able to check/uncheck the task
  const canToggleTask = !isAdminView || (task.assignee && task.assignee.id === currentUserId);
  
  // Get the number of comments
  const commentCount = task.comments.length;
  
  // Check if task has pending change requests
  const hasPendingRequests = task.hasPendingRequests || 
    (task.changeRequests?.some(req => req.status === "pending") || false);

  return (
    <Card className={`task-card ${hasPendingRequests && isAdminView ? "border-amber-300 shadow-amber-100/50 shadow-md" : ""}`}>
      <CardHeader className="p-4 pb-0 flex flex-row items-start justify-between">
        <div className="space-y-1 flex-1">
          <div className="flex items-start gap-2">
            {canToggleTask ? (
              <Checkbox 
                id={`task-${task.id}`}
                checked={task.status === "completed"}
                onCheckedChange={(checked) => onStatusChange(task.id, !!checked)}
              />
            ) : (
              <div className="w-4" /> // Empty space for alignment when checkbox is not shown
            )}
            <div className="flex flex-col">
              <label
                htmlFor={`task-${task.id}`}
                className={`font-medium text-sm cursor-pointer ${
                  task.status === "completed" ? "line-through text-muted-foreground" : ""
                }`}
              >
                {task.title}
                {hasPendingRequests && isAdminView && (
                  <span className="ml-2 inline-flex items-center">
                    <AlertCircle className="h-3.5 w-3.5 text-amber-500" />
                  </span>
                )}
              </label>
              <p className="text-xs text-muted-foreground mt-1">{task.description}</p>
            </div>
          </div>
        </div>
        <Badge className={`${getPriorityColor(task.priority)} capitalize`}>
          {task.priority}
        </Badge>
      </CardHeader>
      <CardContent className="p-4 pt-2">
        <div className="flex flex-wrap gap-y-1 gap-x-4 text-xs text-muted-foreground mt-2">
          <div className="flex items-center">
            <Calendar className="h-3.5 w-3.5 mr-1" />
            <span>{task.dueDate}</span>
          </div>
          <div className="flex items-center">
            <Clock className="h-3.5 w-3.5 mr-1" />
            <span>{task.estimatedTime}</span>
          </div>
          {commentCount > 0 && (
            <div className="flex items-center">
              <MessageSquare className="h-3.5 w-3.5 mr-1" />
              <span>{commentCount}</span>
            </div>
          )}
          
          {hasPendingRequests && isAdminView && (
            <Badge variant="outline" className="bg-amber-50 text-amber-800 border-amber-200 ml-auto">
              <AlertCircle className="h-3 w-3 mr-1" /> Action needed
            </Badge>
          )}
        </div>
      </CardContent>
      <CardFooter className="p-4 pt-0 flex items-center justify-between">
        <Badge variant="outline" className={`text-xs ${getStatusColor(task.status)}`}>
          {task.status === "completed" ? "Completed" : 
           task.status === "in_progress" ? "In Progress" :
           task.status === "overdue" ? "Overdue" : "Pending"}
        </Badge>
        <div className="flex items-center gap-2">
          {task.assignee && (
            <span className="text-sm text-muted-foreground">
              {task.assignee.name.split(' ')[0]} {task.assignee.name.split(' ')[1]?.[0]}.
            </span>
          )}
          <Button variant="ghost" size="sm" onClick={() => onView(task.id)}>
            {isAdminView ? <Edit className="h-4 w-4 mr-1" /> : null}
            {isAdminView ? "Edit" : "View"}
          </Button>
        </div>
      </CardFooter>
    </Card>
  );
};

export default TaskCard;
